import type { FirebaseOptions } from "firebase/app";
import productionFirebaseDefaults from "../firebase/firebaseConfig.json";
import { isLocalApp, isProductionApp, isStagingApp } from "./appEnv";

type FirebaseConfigEnvPrefix = "REACT_APP_FIREBASE" | "REACT_APP_NONPROD_FIREBASE";

const readFirebaseConfigFromEnv = (prefix: FirebaseConfigEnvPrefix): FirebaseOptions | null => {
    const firebaseConfig: FirebaseOptions = {
        apiKey: process.env[`${prefix}_API_KEY`],
        authDomain: process.env[`${prefix}_AUTH_DOMAIN`],
        projectId: process.env[`${prefix}_PROJECT_ID`],
        storageBucket: process.env[`${prefix}_STORAGE_BUCKET`],
        messagingSenderId: process.env[`${prefix}_MESSAGING_SENDER_ID`],
        appId: process.env[`${prefix}_APP_ID`],
    };

    const values = Object.values(firebaseConfig);
    const hasAnyValue = values.some(Boolean);
    const hasAllValues = values.every(Boolean);

    if (hasAnyValue && !hasAllValues) {
        throw new Error(`Incomplete Firebase config for ${prefix}. Set all required REACT_APP Firebase variables before building.`);
    }

    return hasAllValues ? firebaseConfig : null;
};

const readRequiredEnvVar = (name: string): string | null => {
    const value = process.env[name]?.trim();
    return value ? value : null;
};

const productionFirebaseConfig = readFirebaseConfigFromEnv("REACT_APP_FIREBASE") || productionFirebaseDefaults;
const nonProductionFirebaseConfig = readFirebaseConfigFromEnv("REACT_APP_NONPROD_FIREBASE");

if (isLocalApp && !nonProductionFirebaseConfig) {
    console.warn("Non-production Firebase config is not set. Local development will continue using the default Firebase project.");
}

if (isStagingApp && !nonProductionFirebaseConfig) {
    console.warn("Staging Firebase config is not set. Staging builds will continue using the default Firebase project.");
}

export const firebaseConfig = isProductionApp ? productionFirebaseConfig : nonProductionFirebaseConfig || productionFirebaseConfig;

const productionAppCheckSiteKey = readRequiredEnvVar("REACT_APP_FIREBASE_APPCHECK_SITE_KEY") || "6LdNZLoqAAAAADOKd7bCVMjnBNcikasqryFURGGj";
const nonProductionAppCheckSiteKey = readRequiredEnvVar("REACT_APP_NONPROD_FIREBASE_APPCHECK_SITE_KEY");

export const firebaseAppCheckSiteKey = isProductionApp ? productionAppCheckSiteKey : nonProductionAppCheckSiteKey || productionAppCheckSiteKey;
